import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Button, EmbeddedPagination, toast } from '@scholastic/mockingjay';
import SortSolid from '@data/assets/sort-solid.svg';
import SortSolidUp from '@data/assets/sort-solid-up.svg';
import SortSolidDown from '@data/assets/sort-solid-down.svg';
import DocumentCsvIcon from '@data/assets/document-csv-solid.svg';
import MagnifyingGlassIcon from '@data/assets/magnifying-glass-solid.svg';
import { formatTableData } from '@data/utils/format-table-data';
import {
  DATA_ERROR_MESSAGE,
  DATA_UNAVAILABLE_MESSAGE,
  EXPORT_ERROR_MESSAGE,
  type ColumnGroup,
  type TableColumn,
  type TableProps,
  type TableToolbarProps,
} from '@data/components/basic/Table/types';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';

function TableToolbar({
  loading,
  isSearchEmpty,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  onSearchKeyDown,
  onSearchSubmit,
  onExportClick,
}: TableToolbarProps) {
  if (loading) {
    return (
      <div className="flex flex-row items-center border-b border-neutral-400 p-3">
        <LoadingBar className="mx-0 h-12 w-64" />
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-3 p-3 @[500px]/toolbar:flex-row @[500px]/toolbar:items-center @[500px]/toolbar:justify-between @[500px]/toolbar:gap-3">
      <div className="flex min-w-0 flex-1 flex-row justify-between gap-3 @[500px]/toolbar:justify-start">
        <div className="relative min-w-0 flex-1 @[500px]/toolbar:max-w-[320px]">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${
              isSearchEmpty ? 'text-neutral-400' : 'text-neutral-1100'
            }`}
          />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onKeyDown={onSearchKeyDown}
            className="
              h-12 w-full min-w-0 text-ellipsis rounded-sm border border-neutral-400 bg-white pl-10 pr-4 text-base
              font-normal text-neutral-1100 outline-none focus:border-neutral-1100"
          />
        </div>
        <Button
          variant="primary"
          onClick={onSearchSubmit}
          disabled={isSearchEmpty}
          aria-label="Search"
          className="!h-12 !w-12 !min-w-12 !space-x-0 !rounded-full @[500px]/toolbar:!w-28 @[500px]/toolbar:!min-w-[120px] @[500px]/toolbar:!rounded-full @[500px]/toolbar:!px-4"
        >
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="h-6 w-6 @[500px]/toolbar:hidden"
          />
          <span className="hidden @[500px]/toolbar:inline">Search</span>
        </Button>
      </div>
      <Button
        variant="tertiary"
        aria-label="Export table"
        className="!min-w-0 !space-x-0 self-end hover:!bg-white @[500px]/toolbar:self-auto [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1 [@media(hover:hover)]:hover:!bg-neutral-100"
        onClick={onExportClick}
      >
        <DocumentCsvIcon aria-hidden="true" className="h-4 w-4" />
        <span>Export table</span>
      </Button>
    </div>
  );
}

export function Table({
  columns,
  rows,
  totalCount,
  sortColumn,
  sortDirection,
  onSortChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onDrilldown,
  onExportToCSV,
  columnGroups,
  borderedCells,
  loading,
  error,
}: TableProps) {
  const idIndex = columns.findIndex((c) => c.type === 'id');
  const visibleColumns = columns
    .map((col, index) => ({ col, index }))
    .filter(({ col }) => col.type !== 'id' && col.type !== 'group');

  const hasColumnGroups = columnGroups && columnGroups.length > 0;
  const cellBorderClass = borderedCells
    ? 'border-b border-r border-neutral-200 last:border-r-0'
    : '';

  const searchPlaceholder = `Search ${columns?.[1]?.label.toLowerCase() ?? ''}`;
  const isSortable = (col: TableColumn) => col.sortable !== false;

  const handleSortClick = (col: TableColumn) => {
    if (!isSortable(col) || !onSortChange) {
      return;
    }
    // 3-state cycle: not sorted → asc → desc
    if (sortColumn !== col.key) {
      onSortChange(col.key, 'asc');
    } else if (sortDirection === 'asc') {
      onSortChange(col.key, 'desc');
    } else {
      onSortChange(undefined, undefined);
    }
  };

  const renderSortIcon = (col: TableColumn) => {
    if (!isSortable(col)) {
      return null;
    }
    if (sortColumn === col.key) {
      if (sortDirection === 'asc') {
        return <SortSolidUp />;
      }
      if (sortDirection === 'desc') {
        return <SortSolidDown />;
      }
    }
    return <SortSolid />;
  };

  const isSearchEmpty = !searchValue || searchValue.trim() === '';

  const handleSearchSubmit = () => {
    if (isSearchEmpty) {
      return;
    }
    onSearchSubmit?.(searchValue ?? '');
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleExportClick = () => {
    onExportToCSV().catch(() => {
      toast.error(EXPORT_ERROR_MESSAGE, {
        cancel: { label: 'Close' },
        duration: 5000,
      });
    });
  };

  // Cache the last known good totalCount so EmbeddedPagination doesn't briefly
  // see totalCount = 0 during a refetch (which would make its internal
  // `paginationRange.length < 2` early-return unmount the <Select> children
  // and reset the rows-per-page dropdown back to its default).
  const lastTotalCountRef = useRef(totalCount);
  useEffect(() => {
    if (!loading && totalCount > 0) {
      lastTotalCountRef.current = totalCount;
    }
  }, [loading, totalCount]);
  const effectiveTotalCount = loading ? lastTotalCountRef.current : totalCount;

  // Mirrors EmbeddedPagination's own early-return so we don't render an empty
  // wrapper div when EmbeddedPagination would render null.
  const showPagination = !error && page !== 0 && effectiveTotalCount > 10;

  // Skeleton table for the loading state: 2 columns on mobile, 5 on tablet+
  const renderSkeletonTable = (colCount: number) => (
    <table className="w-full min-w-[826px]">
      <thead>
        <tr className="bg-white">
          {Array.from({ length: colCount }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <th key={i} className="p-4">
              <LoadingBar className="mx-0 h-5 w-full" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: pageSize }).map((_, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={rowIdx} className="border-t border-neutral-100">
            {Array.from({ length: colCount }).map((__, colIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <td key={colIdx} className="p-3">
                <LoadingBar className="mx-0 h-5 w-full" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Renders a group header cell. Empty-label cells use <td> (not <th>) so
  // screen readers don't announce an empty column header (WCAG 1.3.1).
  const renderGroupHeaderCell = (group: ColumnGroup, idx: number) => {
    if (group.label) {
      return (
        <th
          key={`${group.label}-${idx}`}
          colSpan={group.colSpan}
          className={`p-4 text-center text-sm font-semibold ${cellBorderClass}`}
        >
          {group.label}
        </th>
      );
    }
    return (
      <td
        key={`spacer-${idx}`}
        colSpan={group.colSpan}
        aria-hidden="true"
        className={`p-4 ${cellBorderClass}`}
      />
    );
  };

  if (error) {
    return (
      <div className="w-full min-w-0 overflow-hidden rounded-lg border border-neutral-400 antialiased @container/toolbar">
        <div className="border-1 mx-4 my-20 rounded-lg border border-dashed border-neutral-400 p-8 text-center text-neutral-700 @[350px]/toolbar:mx-16">
          <span>{DATA_ERROR_MESSAGE}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-hidden rounded-lg border border-neutral-400 antialiased @container/toolbar">
      {/* Search and export container */}
      <TableToolbar
        loading={loading}
        isSearchEmpty={isSearchEmpty}
        searchValue={searchValue}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={onSearchChange}
        onSearchKeyDown={handleSearchKeyDown}
        onSearchSubmit={handleSearchSubmit}
        onExportClick={handleExportClick}
      />
      {/* Column headers and data */}
      <div className="overflow-x-auto border-t border-neutral-400">
        {loading && (
          <>
            <div className="tablet:hidden">{renderSkeletonTable(2)}</div>
            <div className="hidden tablet:block">{renderSkeletonTable(5)}</div>
          </>
        )}
        {!loading && (
          <table className="w-full min-w-[826px]">
            <thead>
              {hasColumnGroups && (
                <tr className="bg-purple-50">
                  {columnGroups.map((group: ColumnGroup, idx: number) =>
                    renderGroupHeaderCell(group, idx)
                  )}
                </tr>
              )}
              <tr className="bg-purple-50">
                {visibleColumns.map(({ col }, visibleIndex) => (
                  <th
                    key={col.key}
                    className={`max-w-[180px] p-4 text-sm font-semibold ${cellBorderClass}`}
                  >
                    <div
                      className={`flex items-center ${
                        visibleIndex === 0
                          ? 'justify-between'
                          : 'justify-end text-right'
                      } gap-2`}
                    >
                      {col.label}
                      {isSortable(col) && (
                        <button
                          className="flex flex-row"
                          type="button"
                          onClick={() => handleSortClick(col)}
                          aria-label={`Sort by ${col.label}`}
                        >
                          <div className="mx-1 w-[15px]">
                            {renderSortIcon(col)}
                          </div>
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {!rows.length ? (
              <tbody>
                <tr>
                  <td colSpan={visibleColumns.length}>
                    <div className="border-1 mx-16 my-20 rounded-lg border border-dashed border-neutral-400 p-8 text-center text-neutral-700">
                      <span>
                        {isSearchEmpty
                          ? DATA_UNAVAILABLE_MESSAGE
                          : `No entries found matching "${searchValue}"`}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {rows.map((row, rowIndex) => {
                  const rowId =
                    idIndex >= 0 ? (row[idIndex] as string | number) : rowIndex;
                  const isLastRow =
                    borderedCells && rowIndex === rows.length - 1;
                  return (
                    <tr
                      key={String(rowId)}
                      className="border-t border-neutral-100 hover:bg-neutral-100 active:bg-neutral-100"
                    >
                      {visibleColumns.map(({ col, index }, visibleIndex) => (
                        <td
                          key={col.key}
                          className={`p-3 text-neutral-900 ${
                            visibleIndex === 0 ? 'text-left' : 'text-right'
                          } ${cellBorderClass}${
                            isLastRow ? ' border-b-0' : ''
                          }`}
                        >
                          {onDrilldown && visibleIndex === 0 ? (
                            <button
                              type="button"
                              onClick={() => onDrilldown(rowId)}
                              className="text-left text-[#0E67E3]"
                            >
                              {formatTableData(row[index], col.type)}
                            </button>
                          ) : (
                            formatTableData(row[index], col.type)
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        )}
      </div>
      {showPagination || loading ? (
        <div className="px-4 py-5 [&_.inline-grid]:grid-rows-1 [&_.inline-grid]:items-center [&_.inline-grid_.row-start-2]:row-start-1">
          <div className={loading || !showPagination ? 'hidden' : ''}>
            <EmbeddedPagination
              totalCount={effectiveTotalCount}
              page={page}
              onPageChange={onPageChange}
              onRowsChange={(newRows) => {
                onPageSizeChange(newRows);
                onPageChange(1);
              }}
            />
          </div>
          {loading && (
            <div className="flex flex-row items-center justify-between gap-3">
              <LoadingBar className="mx-0 h-12 w-full self-center tablet:w-[256px]" />
              <LoadingBar className="mx-0 h-8 w-full self-center tablet:w-[368px]" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
